import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DLQRedriveInfoResult,
  PostDLQRedriveInfoRequest,
  Stage,
} from "../lusca-search-api/generated-src";
import { DlqRedriveInfoApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";

export type DlqRedriveStatus = "unknown" | "loading" | "success" | "error";

export interface DlqRedriveResult {
  status: DlqRedriveStatus;
  error?: string;
}

export interface DlqRedriveInfo {
  redrivenMessages: Map<string, DlqRedriveResult>;
  error: Error | null;
  startRedrive: (messageIds: string[]) => void;
}

interface RedriveRequest {
  messageIds: string[];
  requestId: string;
}

function getStatusForMessage(
  data: DLQRedriveInfoResult,
  messageId: string
): DlqRedriveResult {
  if (!data) return { status: "loading" };

  switch (data.status) {
    case "PENDING":
      return { status: "loading" };
    case "COMPLETED": {
      const itemStatus = data?.result?.redrivenMessages?.[messageId] as string;
      if (itemStatus === "SUCCESS") {
        return { status: "success" };
      }
      return { status: "error", error: `Item error: ${itemStatus}` };
    }
    case "ERROR": {
      const errorMsg = data.result?.error || data.error || "Unknown error";
      const cause = data.result?.cause;
      return {
        status: "error",
        error: cause ? `${errorMsg}: ${cause}` : errorMsg,
      };
    }
    default:
      return { status: "error", error: data.error || "Unknown error" };
  }
}

export default function useFetchDlqRedriveInfo(stage: Stage): DlqRedriveInfo {
  const DlqRedriveApi = DlqRedriveInfoApiFactory();
  const [activeRequests, setActiveRequests] = useState<
    Map<string, RedriveRequest>
  >(new Map());
  const allResults = useRef<Map<string, DlqRedriveResult>>(new Map());
  const startedRequests = useRef<Set<string>>(new Set());
  const [, forceUpdate] = useState({});

  const startRedrive = useCallback((allMessageIds: string[]) => {
    const messageIds = allMessageIds.filter((id) => {
      return !allResults.current.get(id);
    });
    if (messageIds.length === 0) return;

    const requestId = `req-${Date.now()}`;

    messageIds.forEach((id) => {
      allResults.current.set(id, { status: "loading" });
    });

    setActiveRequests((prev) =>
      new Map(prev).set(requestId, { messageIds, requestId })
    );
    forceUpdate({});
  }, []);

  useEffect(() => {
    const cleanups: (() => void)[] = [];

    activeRequests.forEach((request) => {
      const { messageIds, requestId } = request;

      if (startedRequests.current.has(requestId)) return;
      startedRequests.current.add(requestId);

      let executionArn: string | undefined;
      let hasTriggeredImmediateFetch = false;
      let pollInterval: NodeJS.Timeout | null = null;

      const fetchData = async () => {
        try {
          let data: DLQRedriveInfoResult;
          if (executionArn) {
            data = (await DlqRedriveApi.getDLQRedriveInfo(stage, executionArn))
              .data;
          } else {
            data = (
              await DlqRedriveApi.postDLQRedriveInfo(stage, {
                messageIds,
                dryRun: false,
              })
            ).data;
          }

          if (data.executionArn) {
            executionArn = data.executionArn;
          }

          messageIds.forEach((messageId) => {
            const status = getStatusForMessage(data, messageId);
            allResults.current.set(messageId, status);
          });
          forceUpdate({});

          if (data.status === "PENDING") {
            if (!hasTriggeredImmediateFetch) {
              hasTriggeredImmediateFetch = true;
              setTimeout(() => fetchData(), 0);
            } else if (!pollInterval) {
              pollInterval = setInterval(() => fetchData(), 1000);
            }
          } else {
            if (pollInterval) {
              clearInterval(pollInterval);
            }
            setActiveRequests((prev) => {
              const next = new Map(prev);
              next.delete(requestId);
              return next;
            });
          }
        } catch (err) {
          const axiosError = err as {
            response?: { data?: { message?: string } };
          };
          const message =
            axiosError.response?.data?.message || (err as Error).message;

          messageIds.forEach((messageId) => {
            allResults.current.set(messageId, {
              status: "error",
              error: message,
            });
          });
          forceUpdate({});

          if (pollInterval) {
            clearInterval(pollInterval);
          }
          setActiveRequests((prev) => {
            const next = new Map(prev);
            next.delete(requestId);
            return next;
          });
        }
      };

      fetchData();

      cleanups.push(() => {
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      });
    });

    return () => {
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [activeRequests, stage, DlqRedriveApi]);

  return {
    redrivenMessages: new Map(allResults.current),
    error: null,
    startRedrive,
  };
}
